<template>
  <LayupSpinner v-if="isLoading" :size="10" />
  <div v-show="!isLoading">
    <div>
      <h1 class="font-semibold text-xl text-grey-light-900">Client Communication</h1>
      <p class="text-grey-light-600 text-sm">Channels and settings for client communication.</p>
    </div>
    <SectionDivider class="my-4" />
    <form @submit.prevent="onSubmit">
      <div class="grid gap-4 md:grid-cols-5 sm:grid-cols-1">
        <div class="col-span-2">
          <h5 class="font-semibold text-grey-light-900">Communication Channels</h5>
          <p class="text-grey-light-600 text-sm">Choose how you would like to communicate with your clients.</p>
        </div>
        <div class="col-span-2">
          <FormField v-slot="{ errors }" name="commChannels">
            <FormItem>
              <div class="flex flex-col gap-4">
                <FormField v-slot="{ value, handleChange }" type="checkbox" name="commChannels.email">
                  <FormItem>
                    <div class="flex gap-2">
                      <FormControl>
                        <CheckboxComponent :model-value="value" @update:model-value="handleChange" />
                      </FormControl>
                      <FormLabel class="font-medium">
                        <div class="flex flex-col gap-2">
                          <span class="font-medium">Email</span>
                          <span class="text-grey-light-600 text-sm">
                            Send order notifications and reminders to your clients via email.
                          </span>
                        </div>
                      </FormLabel>
                    </div>
                    <FormMessage />
                  </FormItem>
                </FormField>
                <FormField v-slot="{ value, handleChange }" type="checkbox" name="commChannels.sms">
                  <FormItem>
                    <div class="flex gap-2">
                      <FormControl>
                        <CheckboxComponent :model-value="value" @update:model-value="handleChange" />
                      </FormControl>
                      <FormLabel>
                        <div class="flex flex-col gap-2">
                          <span class="font-medium">SMS</span>
                          <span class="text-grey-light-600 text-sm">
                            Send order notifications and reminders to your clients via SMS.
                          </span>
                        </div>
                      </FormLabel>
                    </div>
                    <FormMessage />
                  </FormItem>
                </FormField>
              </div>
              <FormMessage v-if="errors">{{ typeof errors === 'string' ? errors : errors[0] }}</FormMessage>
            </FormItem>
          </FormField>
        </div>
      </div>
      <SectionDivider class="my-4" />
      <div class="grid gap-4 md:grid-cols-5 sm:grid-cols-1">
        <div class="col-span-2">
          <h5 class="font-semibold text-grey-light-900">Communication</h5>
          <p class="text-grey-light-600 text-sm">Configure how and when notifications are sent to clients.</p>
        </div>
        <div class="col-span-2">
          <div class="flex flex-col gap-4">
            <FormField v-slot="{ value, handleChange }" name="commSettings.orderExpiryReminder">
              <FormItem class="flex gap-2 items-center">
                <FormControl>
                  <SwitchComponent :model-value="value" @update:model-value="handleChange" />
                </FormControl>
                <FormLabel class="!mt-0">
                  <div class="flex flex-col gap-2">
                    <span class="font-medium">Order expiry reminder</span>
                    <span class="text-grey-light-600 text-sm">Automatically cancel payment plans.</span>
                  </div>
                </FormLabel>
              </FormItem>
            </FormField>
            <div v-if="isOrderExpiryEnabled" class="grid grid-cols-5">
              <div class="col-span-2">
                <FormField v-slot="{ componentField }" name="commSettings.daysBeforeExpiry">
                  <FormItem>
                    <FormLabel>Days before expiry</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="Months" v-bind="componentField" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                </FormField>
              </div>
            </div>
            <FormField v-slot="{ value, handleChange }" name="commSettings.orderActivation">
              <FormItem class="flex gap-2 items-center">
                <FormControl>
                  <SwitchComponent :model-value="value" @update:model-value="handleChange" />
                </FormControl>
                <FormLabel class="!mt-0">
                  <div class="flex flex-col gap-2">
                    <span class="font-medium">Order Activated</span>
                    <span class="text-grey-light-600 text-sm">Send order activated message to customers.</span>
                  </div>
                </FormLabel>
              </FormItem>
            </FormField>
            <FormField v-slot="{ value, handleChange }" name="commSettings.orderCancellation">
              <FormItem class="flex gap-2 items-center">
                <FormControl>
                  <SwitchComponent :model-value="value" @update:model-value="handleChange" />
                </FormControl>
                <FormLabel class="!mt-0">
                  <div class="flex flex-col gap-2">
                    <span class="font-medium">Cancellation</span>
                    <span class="text-grey-light-600 text-sm">Send cancellation message to customers.</span>
                  </div>
                </FormLabel>
              </FormItem>
            </FormField>
            <FormField v-slot="{ value, handleChange }" name="commSettings.orderRefunded">
              <FormItem class="flex gap-2 items-center">
                <FormControl>
                  <SwitchComponent :model-value="value" @update:model-value="handleChange" />
                </FormControl>
                <FormLabel class="!mt-0">
                  <div class="flex flex-col gap-2">
                    <span class="font-medium">Refund</span>
                    <span class="text-grey-light-600 text-sm">Send refund message to customers.</span>
                  </div>
                </FormLabel>
              </FormItem>
            </FormField>
          </div>
        </div>
      </div>
      <SectionDivider class="my-4" />
      <div class="flex justify-end gap-4 mt-4 mr-8">
        <Button class="w-auto" type="submit">Save</Button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import SectionDivider from '@/components/common/SectionDivider.vue';
import { Button } from '@/components/ui/button';
import CheckboxComponent from '@/components/ui/checkbox/CheckboxComponent.vue';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import SwitchComponent from '@/components/ui/switch/SwitchComponent.vue';
import { NetworkError } from '@/errors';
import { useMerchantStore } from '@/stores/merchants';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { computed, onMounted, ref, watch } from 'vue';
import { useToast } from 'vue-toast-notification';
import { z } from 'zod';
import type { ClientCommunication } from '../utils';

const props = defineProps<{
  settings: ClientCommunication;
  merchantId: string;
}>();

const isLoading = ref(false);

const clientCommFormSchema = toTypedSchema(
  z.object({
    commChannels: z
      .object({
        email: z.boolean().optional(),
        sms: z.boolean().optional(),
      })
      .refine((value) => value.email || value.sms, {
        message: 'At least one communication channel must be enabled',
      }),

    commSettings: z
      .object({
        orderExpiryReminder: z.boolean().optional(),
        daysBeforeExpiry: z.preprocess((val) => {
          const num = Number(val);
          return isNaN(num) ? undefined : num;
        }, z.number().optional()),
        orderActivation: z.boolean().optional(),
        orderCancellation: z.boolean().optional(),
        orderRefunded: z.boolean().optional(),
      })
      .superRefine((values, ctx) => {
        if (values.orderExpiryReminder) {
          if (values.daysBeforeExpiry === undefined) {
            ctx.addIssue({
              code: 'custom',
              message: 'Days before expiry is required when order expiry reminder is enabled',
              path: ['daysBeforeExpiry'],
            });
          } else if (values.daysBeforeExpiry <= 0) {
            ctx.addIssue({
              code: 'custom',
              message: 'Days before expiry must be greater than 0',
              path: ['daysBeforeExpiry'],
            });
          }
        }
      }),
  }),
);

const emit = defineEmits(['save:merchant']);
const toast = useToast();
const merchantStore = useMerchantStore();
const form = useForm({
  validationSchema: clientCommFormSchema,
});

watch(
  () => props.settings,
  (newSettings) => {
    if (!newSettings) {
      isLoading.value = false;
      return;
    }

    isLoading.value = true;

    if (newSettings?.channels) {
      if (newSettings.channels.includes('EMAIL')) form.setFieldValue('commChannels.email', true);
      if (newSettings.channels.includes('SMS')) form.setFieldValue('commChannels.sms', true);
    }

    form.setValues({
      commSettings: {
        orderExpiryReminder: newSettings?.messages?.orderExpiryReminder?.enabled || false,
        daysBeforeExpiry: newSettings?.messages?.orderExpiryReminder?.daysBeforeExpiryInDays || 0,
        orderActivation: newSettings?.messages?.orderActivated?.enabled || false,
        orderCancellation: newSettings?.messages?.cancellation?.enabled || false,
        orderRefunded: newSettings?.messages?.refund?.enabled || false,
      },
    });

    form.validate({ mode: 'silent' });
    isLoading.value = false;
  },
  { immediate: true, deep: true },
);

onMounted(() => {
  watch(
    () => form.values.commSettings?.orderExpiryReminder,
    (state) => {
      if (state) {
        form.setFieldValue(
          'commSettings.daysBeforeExpiry',
          props.settings?.messages?.orderExpiryReminder?.daysBeforeExpiryInDays || 0,
        );
      }
    },
  );
});

const isOrderExpiryEnabled = computed(() => form?.values?.commSettings?.orderExpiryReminder);

const onSubmit = async () => {
  isLoading.value = true;
  const { values, errors, valid } = await form.validate();

  // Need to manually check errors because for some reason vee-validate doesn't set errors on the form when submitted even if valid is false
  if (Object.keys(errors).length > 0) {
    Object.entries(errors).forEach(([key, value]) => {
      form.setFieldError(key as keyof typeof errors, value);
    });
    return;
  }
  if (valid) {
    try {
      isLoading.value = true;
      if (props.merchantId === '') {
        toast.error('Merchant ID is not set. Please try reloading the page', { position: 'top' });
        throw new Error('Merchant ID is required.');
      }
      const settings: ClientCommunication = { ...props.settings };
      const messages = settings?.messages;
      const channels: string[] = [];

      if (
        !values?.commSettings?.orderExpiryReminder &&
        !values?.commSettings?.orderActivation &&
        !values?.commSettings?.orderCancellation &&
        !values?.commSettings?.orderRefunded
      ) {
        settings!.channels = channels;
      } else {
        if (values.commChannels?.email) channels.push('EMAIL');
        if (values.commChannels?.sms) channels.push('SMS');
        settings!.channels = channels;
      }

      messages!.orderExpiryReminder!.enabled = values?.commSettings?.orderExpiryReminder || false;
      messages!.orderExpiryReminder!.daysBeforeExpiryInDays = values?.commSettings?.daysBeforeExpiry || 0;
      messages!.orderActivated!.enabled = values?.commSettings?.orderActivation || false;
      messages!.cancellation!.enabled = values?.commSettings?.orderCancellation || false;
      messages!.refund!.enabled = values?.commSettings?.orderRefunded || false;

      const response = await merchantStore.saveMerchantSettings({
        id: props.merchantId,
        modelMerchantRequest: { communication: settings },
      });
      emit('save:merchant', response);

      addMetric('settings-page-client-comms', { status: 'success', channel: 'merchant', method: 'saveMerchant' });
    } catch (error) {
      if (error instanceof NetworkError) {
        createWarning(error);
        addMetric('settings-page-client-comms', {
          status: 'network_error',
          channel: 'merchant',
          method: 'saveMerchant',
        });
      } else {
        createException(error);
        addMetric('settings-page-client-comms', { status: 'error', channel: 'merchant', method: 'saveMerchant' });
      }
    }
  }
};
</script>
